import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { formatCurrency } from 'ddc-intl';
import { useLabels } from 'wsm-common-data';

import SingleItem from './SingleItem';

const GridStylePackages = ({
	packages,
	options,
	accessories,
	widgetInfo,
	locale,
	isModal,
	onOpenModal,
	isEnableDisclaimer
}) => {
	const labels = useLabels();

	const totalItemsCount = useMemo(() => {
		return (
			packages.length + (options.length > 0 ? 1 : 0) + accessories.length
		);
	}, [packages, options, accessories]);

	const totalOptionsPrice = useMemo(() => {
		const price = options.reduce((sum, item) => sum + (item.price || 0), 0);
		return formatCurrency(price, locale);
	}, [options, locale]);

	const columnClasses = useMemo(() => {
		if (!isModal) {
			if (totalItemsCount === 1) {
				return 'col-xs-12 col-md-12';
			} else if (totalItemsCount === 2 || totalItemsCount === 4) {
				return 'col-xs-12 col-md-6';
			}
			return 'col-xs-12 col-md-4';
		}
		return 'w-100';
	}, [isModal, totalItemsCount]);

	if (
		packages.length === 0 &&
		options.length === 0 &&
		accessories.length === 0
	) {
		return null;
	}

	return (
		<div className="d-flex flex-wrap list-unstyled row option-list">
			{packages.map((singlePackage, index) => {
				const { name, price, subitems } = singlePackage;
				const formattedPrice = formatCurrency(price, locale);
				const isFirstItem = index === 0;
				return (
					<div key={name} className={`${columnClasses} mb-5`}>
						<SingleItem
							itemType="package"
							index={index}
							itemName={name}
							subItems={subitems}
							itemPrice={formattedPrice}
							widgetInfo={widgetInfo}
							totalItemsCount={totalItemsCount}
							isModal={isModal}
							onOpenModal={onOpenModal}
							isFirstItem={isFirstItem}
							isEnableDisclaimer={isEnableDisclaimer}
						/>
					</div>
				);
			})}
			{options && options.length > 0 ? (
				<div key="options-key" className={`${columnClasses} mb-5`}>
					<SingleItem
						itemType="option"
						itemName={labels.get('MORE_PRE_INSTALLED_ITEMS')}
						subItems={options}
						widgetInfo={widgetInfo}
						totalItemsCount={totalItemsCount}
						isModal={isModal}
						onOpenModal={onOpenModal}
						isFirstItem={packages.length === 0}
						isEnableDisclaimer={isEnableDisclaimer}
						itemPrice={totalOptionsPrice}
					/>
				</div>
			) : null}

			{accessories.map((accessory, index) => {
				const { name, price } = accessory;
				const formattedPrice = formatCurrency(price, locale);
				const isFirstItem =
					packages.length === 0 &&
					options.length === 0 &&
					index === 0;
				return (
					<div key={name} className={`${columnClasses} mb-5`}>
						<SingleItem
							itemType="accessories"
							index={index}
							itemName={name}
							itemPrice={formattedPrice}
							widgetInfo={widgetInfo}
							totalItemsCount={totalItemsCount}
							isModal={isModal}
							onOpenModal={onOpenModal}
							isFirstItem={isFirstItem}
							isEnableDisclaimer={isEnableDisclaimer}
						/>
					</div>
				);
			})}
		</div>
	);
};

GridStylePackages.defaultProps = {
	locale: 'en_US',
	packages: [],
	widgetInfo: {
		windowId: 'packages-options1',
		widgetName: 'ws-packages-options'
	}
};

GridStylePackages.propTypes = {
	locale: PropTypes.string,
	packages: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired,
			subitems: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
		})
	),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	accessories: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string,
		widgetName: PropTypes.string
	}),
	isModal: PropTypes.bool.isRequired,
	onOpenModal: PropTypes.func.isRequired,
	isEnableDisclaimer: PropTypes.bool.isRequired
};

export default GridStylePackages;
